import React, { FC, useMemo, useState, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ShiftEditableItemCard } from "@/app/application/admin-menu/settings/ShiftEditableItemCard";
import { ShiftSettingsSection } from "@/app/application/admin-menu/settings/ShiftSettingsSection";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { Spinner } from "@/shared/ui/Spinner";
import {
	Add as AddIcon,
	FormatColorReset as FormatColorResetIcon,
	Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
	Box,
	Button,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { ShiftName } from "@/model/Application/Settings/SettingsResponseTypes";

import {
	useMutateCreateShiftName,
	useMutateDeleteShiftName,
	useMutateUpdateShiftName,
} from "@/service/Settings/Mutations/ShiftSettingsMutations";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(customParseFormat);

const DEFAULT_TIME = "00:00:00";
const TIME_FORMAT = "HH:mm:ss";
const TIME_INPUT_FORMATS = [TIME_FORMAT, "HH:mm"];
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{6})$/;

const toStringValue = (value: unknown): string => {
	if (typeof value === "string") return value;
	if (
		typeof value === "number" ||
		typeof value === "boolean" ||
		typeof value === "bigint"
	) {
		return String(value);
	}

	return "";
};

const normalizeHexColor = (value: unknown): string => {
	const s = toStringValue(value).trim();
	return HEX_COLOR_REGEX.test(s) ? s : "";
};

const parseTimeValue = (value: unknown): Dayjs | null => {
	if (!value) return null;

	if (typeof value === "string") {
		const trimmed = value.trim();

		if (!trimmed) return null;

		const timeOnly = dayjs(trimmed, TIME_INPUT_FORMATS, true);
		if (timeOnly.isValid()) return timeOnly;

		const dateTime = dayjs(trimmed);
		return dateTime.isValid() ? dateTime : null;
	}

	const parsed = dayjs(value as dayjs.ConfigType);
	return parsed.isValid() ? parsed : null;
};

const toTimeString = (value: unknown): string => {
	return parseTimeValue(value)?.format(TIME_FORMAT) ?? DEFAULT_TIME;
};

const toColorInputValue = (value: unknown): string => {
	return normalizeHexColor(value) || "#000000";
};

const toApiPayload = (row: Partial<ShiftName>) => ({
	name: toStringValue(row.Name),
	startTime: toTimeString(row.StartTime),
	endTime: toTimeString(row.EndTime),
	color: normalizeHexColor(row.Color),
});

interface ShiftNameSettingsProps {
	data: ShiftName[];
}

export const ShiftNameSettings: FC<ShiftNameSettingsProps> = ({ data }) => {
	const translation: Language | null = useContext(TranslationContext);
	const { createShiftName } = useMutateCreateShiftName();
	const { updateShiftName } = useMutateUpdateShiftName();
	const { deleteShiftName } = useMutateDeleteShiftName();

	const [editingId, setEditingId] = useState<number | "new" | null>(null);
	const [saving, setSaving] = useState(false);

	const rows = useMemo(() => {
		return (data ?? []).map((r) => ({
			...r,
			Color: normalizeHexColor(r.Color),
		}));
	}, [data]);

	const handleCreate = async (item: Partial<ShiftName>) => {
		const payload = toApiPayload(item);
		if (!payload.name.trim()) return;
		setSaving(true);
		try {
			await createShiftName({
				Name: payload.name,
				StartTime: payload.startTime,
				EndTime: payload.endTime,
				Color: payload.color,
			});
			AlertRequest.success();
			setEditingId(null);
		} catch (e) {
			console.error(e);
			AlertRequest.failed();
		} finally {
			setSaving(false);
		}
	};

	const handleUpdate = async (item: ShiftName, updates: Partial<ShiftName>) => {
		const payload = toApiPayload({ ...item, ...updates });
		if (!payload.name.trim()) return;
		setSaving(true);
		try {
			await updateShiftName({
				Id: item.Id,
				Name: payload.name,
				StartTime: payload.startTime,
				EndTime: payload.endTime,
				Color: payload.color,
			});
			AlertRequest.success();
			setEditingId(null);
		} catch (e) {
			console.error(e);
			AlertRequest.failed();
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await AlertRequest.confirmAsync(
				deleteShiftName as (shiftNameId: number) => void,
				id,
				"this shift rotation",
			);
			setEditingId(null);
		} catch (e) {
			console.error(e);
			AlertRequest.failed();
		}
	};

	const handleAddClick = () => setEditingId("new");

	if (!data) return <Spinner text={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftNameSettings.L173C34.LoadingSettings", "Loading settings")} />;

	return (
		<ShiftSettingsSection
			title={
				<Translate String="Shift.Event.ShiftRotation">Shift Rotation</Translate>
			}
			icon={<ScheduleIcon />}
			action={
				<Button
					variant="outlined"
					color="inherit"
					startIcon={<AddIcon />}
					onClick={handleAddClick}
					sx={{
						height: 32,
						minHeight: 32,
						px: 1.5,
						textTransform: "none",
						fontWeight: 600,
						borderColor: "divider",
						bgcolor: "background.paper",
						boxShadow: "none",
						whiteSpace: "nowrap",
					}}
				>
					<Translate String="Settings.Shift.ShiftName.AddShiftName">
						Add Shift Rotation
					</Translate>
				</Button>
			}
			sx={{ width: "100%", height: "100%" }}
			headerSx={{
				alignItems: "center",
				pb: 2,
				borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
				mb: 2.5,
			}}
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Stack spacing={1.5}>
					{rows.map((row) => (
						<ShiftNameItem
							key={row.Id}
							row={row}
							isEditing={editingId === row.Id}
							onStartEdit={() => setEditingId(row.Id)}
							onCancel={() => setEditingId(null)}
							onSave={(updates) => {
								void handleUpdate(row, updates);
							}}
							onDelete={() => {
								void handleDelete(row.Id);
							}}
							saving={saving}
						/>
					))}
					{editingId === "new" && (
						<ShiftNameEditForm
							initial={{
								Name: "",
								StartTime: DEFAULT_TIME,
								EndTime: DEFAULT_TIME,
								Color: "",
							}}
							onSave={(item) => {
								void handleCreate(item);
							}}
							onCancel={() => setEditingId(null)}
							saving={saving}
							isNew
						/>
					)}
					{rows.length === 0 && editingId !== "new" && (
						<Box py={4} textAlign="center" sx={{ color: "text.secondary" }}>
							<Typography variant="body2">
								No shift rotations yet. Add one to get started.
							</Typography>
						</Box>
					)}
				</Stack>
			</LocalizationProvider>
		</ShiftSettingsSection>
	);
};

interface ShiftNameItemProps {
	row: ShiftName;
	isEditing: boolean;
	onStartEdit: () => void;
	onCancel: () => void;
	onSave: (updates: Partial<ShiftName>) => void;
	onDelete: () => void;
	saving: boolean;
}

const ShiftNameItem: FC<ShiftNameItemProps> = ({
	row,
	isEditing,
	onStartEdit,
	onCancel,
	onSave,
	onDelete,
	saving,
}) => {
	const color = normalizeHexColor(row.Color);
	const hasColor = Boolean(color);

	const [editState, setEditState] = useState({
		Name: row.Name,
		StartTime: row.StartTime ?? DEFAULT_TIME,
		EndTime: row.EndTime ?? DEFAULT_TIME,
		Color: normalizeHexColor(row.Color),
	});

	React.useEffect(() => {
		if (isEditing) {
			setEditState({
				Name: row.Name,
				StartTime: row.StartTime ?? DEFAULT_TIME,
				EndTime: row.EndTime ?? DEFAULT_TIME,
				Color: normalizeHexColor(row.Color),
			});
		}
	}, [isEditing, row]);

	const handleSave = () => {
		onSave(editState);
	};

	const viewContent = (
		<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
			<Typography variant="body1" fontWeight={500}>
				{row.Name}
			</Typography>
			<Stack direction="row" spacing={1} alignItems="center">
				<Box
					sx={{
						width: 10,
						height: 10,
						borderRadius: "50%",
						bgcolor: hasColor ? color : "transparent",
						border: (theme) => {
							const borderColor =
								theme.palette.mode === "dark"
									? "rgba(255,255,255,0.28)"
									: "rgba(0,0,0,0.25)";
							return `1px ${hasColor ? "solid" : "dashed"} ${borderColor}`;
						},
						backgroundImage: hasColor
							? "none"
							: "repeating-conic-gradient(rgba(0,0,0,0.12) 0% 25%, transparent 0% 50%)",
						backgroundSize: hasColor ? undefined : "6px 6px",
					}}
				/>
				<Typography variant="body2" color="text.secondary">
					{toTimeString(row.StartTime)} – {toTimeString(row.EndTime)}
				</Typography>
			</Stack>
		</Stack>
	);

	return (
		<ShiftEditableItemCard
			isEditing={isEditing}
			onEdit={onStartEdit}
			onSave={handleSave}
			onCancel={onCancel}
			onDelete={onDelete}
			viewContent={viewContent}
			editContent={
				<ShiftNameEditFields
					state={editState}
					onChange={setEditState}
					onKeySave={handleSave}
					onKeyCancel={onCancel}
				/>
			}
			saving={saving}
		/>
	);
};

interface ShiftNameEditFormProps {
	initial: ShiftNameEditState;
	onSave: (item: ShiftNameEditState) => void;
	onCancel: () => void;
	saving: boolean;
	isNew: boolean;
}

const ShiftNameEditForm: FC<ShiftNameEditFormProps> = ({
	initial,
	onSave,
	onCancel,
	saving,
	isNew,
}) => {
	const [state, setState] = useState(initial);

	const handleSave = () => {
		onSave(state);
	};

	return (
		<ShiftEditableItemCard
			isEditing
			onEdit={() => {}}
			onSave={handleSave}
			onCancel={onCancel}
			onDelete={() => {}}
			viewContent={null}
			editContent={
				<ShiftNameEditFields
					state={state}
					onChange={setState}
					onKeySave={handleSave}
					onKeyCancel={onCancel}
				/>
			}
			isNew={isNew}
			saving={saving}
		/>
	);
};

type ShiftNameEditState = {
	Name: string;
	StartTime: string;
	EndTime: string;
	Color: string;
};

interface ShiftNameEditFieldsProps {
	state: ShiftNameEditState;
	onChange: (s: ShiftNameEditState) => void;
	onKeySave: () => void;
	onKeyCancel: () => void;
}

const INPUT_HEIGHT = 40;

const ShiftNameEditFields: FC<ShiftNameEditFieldsProps> = ({
	state,
	onChange,
	onKeySave,
	onKeyCancel,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const color = normalizeHexColor(state.Color);
	const hasColor = Boolean(color);
	const startParsed = parseTimeValue(state.StartTime);
	const endParsed = parseTimeValue(state.EndTime);

	const inputSx = {
		"& .MuiOutlinedInput-root": {
			bgcolor: (theme: { palette: { mode: string } }) =>
				theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
			"& fieldset": { borderColor: "transparent" },
		},
	};

	return (
		<Stack
			direction="row"
			spacing={2}
			alignItems="center"
			flexWrap="wrap"
			sx={{ width: "100%", gap: 2 }}
		>
			<TextField
				autoFocus
				size="small"
				placeholder={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftNameSettings.L446C17.Name", "Name")}
				value={state.Name}
				onChange={(e) => onChange({ ...state, Name: e.target.value })}
				onKeyDown={(e) => {
					if (e.key === "Enter") onKeySave();
					if (e.key === "Escape") onKeyCancel();
				}}
				sx={{
					...inputSx,
					minWidth: 140,
					flex: "1 1 120px",
					"& .MuiInputBase-root": { height: INPUT_HEIGHT },
				}}
			/>
			<TimePicker
				label={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftNameSettings.L461C11.Start", "Start")}
				value={startParsed}
				onChange={(v: Dayjs | null) =>
					onChange({
						...state,
						StartTime: v?.isValid() ? v.format(TIME_FORMAT) : DEFAULT_TIME,
					})
				}
				slotProps={{
					textField: {
						size: "small",
						sx: {
							...inputSx,
							width: 110,
							"& .MuiInputBase-root": { height: INPUT_HEIGHT },
						},
					},
				}}
			/>
			<TimePicker
				label={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftNameSettings.L481C11.End", "End")}
				value={endParsed}
				onChange={(v: Dayjs | null) =>
					onChange({
						...state,
						EndTime: v?.isValid() ? v.format(TIME_FORMAT) : DEFAULT_TIME,
					})
				}
				slotProps={{
					textField: {
						size: "small",
						sx: {
							...inputSx,
							width: 110,
							"& .MuiInputBase-root": { height: INPUT_HEIGHT },
						},
					},
				}}
			/>
			<Stack
				direction="row"
				spacing={1.5}
				alignItems="center"
				sx={{ height: INPUT_HEIGHT }}
			>
				<Box
					sx={{
						position: "relative",
						width: INPUT_HEIGHT,
						height: INPUT_HEIGHT,
						p: 0.5,
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: 1,
						overflow: "hidden",
						cursor: "pointer",
						flexShrink: 0,
						bgcolor: hasColor ? color : "transparent",
						backgroundImage: hasColor
							? "none"
							: "repeating-conic-gradient(rgba(0,0,0,0.12) 0% 25%, transparent 0% 50%)",
						backgroundSize: hasColor ? undefined : "8px 8px",
					}}
				>
					<input
						type="color"
						value={toColorInputValue(state.Color)}
						onChange={(e) => onChange({ ...state, Color: e.target.value })}
						style={{
							position: "absolute",
							inset: 0,
							opacity: 0,
							cursor: "pointer",
							width: "100%",
							height: "100%",
							border: "none",
							padding: 0,
						}}
					/>
				</Box>
				<TextField
					size="small"
					value={state.Color}
					onChange={(e) => onChange({ ...state, Color: e.target.value })}
					onBlur={() =>
						onChange({ ...state, Color: normalizeHexColor(state.Color) })
					}
					placeholder={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftNameSettings.L547C18.RRGGBB", "#RRGGBB")}
					sx={{
						...inputSx,
						width: 90,
						"& .MuiInputBase-root": { height: INPUT_HEIGHT },
					}}
				/>
				<Tooltip title={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftNameSettings.L554C20.ClearColor", "Clear color")}>
					<span>
						<IconButton
							size="small"
							onClick={() => onChange({ ...state, Color: "" })}
							disabled={!hasColor}
							sx={{ color: "text.secondary" }}
							aria-label={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftNameSettings.L561C19.ClearColor", "Clear color")}
						>
							<FormatColorResetIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Stack>
		</Stack>
	);
};
